import React, { useState } from 'react';
import { MAP_SIZES } from '../../config.js';

const OptionsMenu = ({ onClose, onUpdateConfig }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedPreset, setSelectedPreset] = useState('MEDIUM');

    const handleBackClick = () => {
        console.log('Back button clicked');
        onClose();
    };

    const tabs = [
        { name: 'Game Systems', content: <GameSystemsTab /> },
        { name: 'World Settings', content: <WorldSettingsTab 
            selectedPreset={selectedPreset}
            onPresetChange={(preset) => {
                setSelectedPreset(preset);
                const mapSize = MAP_SIZES[preset];
                onUpdateConfig('MAP_WIDTH', mapSize.width);
                onUpdateConfig('MAP_HEIGHT', mapSize.height);
            }}
        /> },
        { name: 'Player Settings', content: <PlayerSettingsTab /> },
        { name: 'Performance', content: <PerformanceTab /> }
    ];

    return React.createElement('div', { className: 'options-menu' },
        React.createElement('div', { className: 'options-header' },
            React.createElement('h2', null, 'Options')
        ),
        React.createElement('div', { className: 'options-tabs' },
            tabs.map((tab, index) => 
                React.createElement('button', {
                    key: tab.name,
                    className: `tab-button ${activeTab === index ? 'active' : ''}`,
                    onClick: () => setActiveTab(index)
                }, tab.name)
            )
        ),
        React.createElement('div', { className: 'options-content' },
            tabs[activeTab].content
        ),
        React.createElement('button', {
            className: 'back-button',
            onClick: handleBackClick,
            style: { cursor: 'pointer' }
        }, 'Back')
    );
};

const GameSystemsTab = () => {
    const systems = [
        { name: 'Time System', key: 'time' },
        { name: 'Wetness System', key: 'wetness' },
        { name: 'Plant System', key: 'plants' },
        { name: 'Animal System', key: 'animals' },
        { name: 'Lighting System', key: 'lighting' }
    ];

    return React.createElement('div', { className: 'systems-list' },
        systems.map(system => 
            React.createElement(ToggleOption, {
                key: system.key,
                name: system.name
            })
        )
    );
};

const WorldSettingsTab = ({ selectedPreset, onPresetChange }) => {
    return React.createElement('div', { className: 'world-settings' },
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Island Preset'),
            React.createElement('select', {
                value: selectedPreset,
                onChange: (e) => onPresetChange(e.target.value)
            },
                Object.entries(MAP_SIZES).map(([key, value]) =>
                    React.createElement('option', {
                        key: key,
                        value: key
                    }, value.name)
                )
            )
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Map Width'),
            React.createElement(NumberInput, {
                name: 'MAP_WIDTH',
                min: 50,
                max: 500
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Map Height'),
            React.createElement(NumberInput, {
                name: 'MAP_HEIGHT',
                min: 50,
                max: 500
            })
        )
    );
};

const PlayerSettingsTab = () => {
    return React.createElement('div', { className: 'player-settings' },
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Player Speed'),
            React.createElement(NumberInput, {
                name: 'PLAYER_SPEED',
                min: 1,
                max: 10
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Player Acceleration'),
            React.createElement(NumberInput, {
                name: 'PLAYER_ACCELERATION',
                min: 0.1,
                max: 2,
                step: 0.1
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Player Deceleration'),
            React.createElement(NumberInput, {
                name: 'PLAYER_DECELERATION',
                min: 0.1,
                max: 2,
                step: 0.1
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Player Max Speed'),
            React.createElement(NumberInput, {
                name: 'PLAYER_MAX_SPEED',
                min: 1,
                max: 20
            })
        )
    );
};

const PerformanceTab = () => {
    return React.createElement('div', { className: 'performance-settings' },
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Render Radius'),
            React.createElement(NumberInput, {
                name: 'RENDER_RADIUS',
                min: 5,
                max: 20
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Sub Tile Size'),
            React.createElement(NumberInput, {
                name: 'SUB_TILE_SIZE',
                min: 4,
                max: 16
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Entity Base Radius'),
            React.createElement(NumberInput, {
                name: 'ENTITY_BASE_RADIUS',
                min: 8,
                max: 32
            })
        ),
        React.createElement('div', { className: 'setting-group' },
            React.createElement('label', null, 'Tile Scale'),
            React.createElement(NumberInput, {
                name: 'TILE_SCALE',
                min: 1,
                max: 4
            })
        )
    );
};

const ToggleOption = ({ name }) => {
    const [isEnabled, setIsEnabled] = useState(false);

    return React.createElement('div', { className: 'toggle-option' },
        React.createElement('label', null, name),
        React.createElement('button', {
            className: `toggle-button ${isEnabled ? 'enabled' : ''}`,
            onClick: () => setIsEnabled(!isEnabled)
        }, isEnabled ? 'ON' : 'OFF')
    );
};

const NumberInput = ({ name, min, max, step = 1 }) => {
    const [value, setValue] = useState(min);

    const handleChange = (newValue) => {
        if (newValue >= min && newValue <= max) {
            setValue(newValue);
        }
    };

    return React.createElement('div', { className: 'number-input' },
        React.createElement('button', {
            onClick: () => handleChange(value - step)
        }, '-'),
        React.createElement('input', {
            type: 'number',
            value: value,
            min: min,
            max: max,
            step: step,
            onChange: (e) => handleChange(Number(e.target.value))
        }),
        React.createElement('button', {
            onClick: () => handleChange(value + step)
        }, '+')
    );
};

export default OptionsMenu; 